/* ============================================================
   SK Cart — shared behaviour for the cart page (.sk-cart) and the
   bag drawer (.sk-cart-drawer).

   - Quantity / remove / cross-sell add use Shopify's real cart AJAX
     API, then re-render both surfaces via the Section Rendering API.
   - Free-shipping progress bar + delivery/pickup toggle are display-
     only hints (Shopify calculates real shipping at checkout).
   - Promo codes apply at checkout via the /discount/CODE URL.
   - The header bag link is intercepted to open the drawer (with a
     graceful fallback to the /cart page if JS is unavailable).
   ============================================================ */
(function () {
  "use strict";
  if (window.SKCartLoaded) return;
  window.SKCartLoaded = true;

  var $  = function (s, r) { return (r || document).querySelector(s); };
  var $$ = function (s, r) { return Array.prototype.slice.call((r || document).querySelectorAll(s)); };

  /* ── config (from either surface's JSON island) ── */
  function readConfig() {
    var node = $("[data-sk-cart-config]");
    var cfg = { threshold: 0, moneySymbol: "$", cutoffWeekday: 2, cutoffHour: 23, cutoffMinute: 59, promoToCheckout: true, routes: {} };
    if (node) { try { cfg = Object.assign(cfg, JSON.parse(node.textContent)); } catch (e) {} }
    return cfg;
  }
  var cfg = readConfig();

  function money(cents) {
    var n = (Number(cents) || 0) / 100;
    var r = Math.round(n * 100) / 100;
    return cfg.moneySymbol + (Number.isInteger(r) ? r : r.toFixed(2));
  }
  function jsUrl(route, fallback) {
    var base = route || fallback;
    return base.indexOf(".js") === -1 ? base + ".js" : base;
  }

  var state = { fulfil: "delivery", promo: null, drawerOpen: false, lastFocus: null, freeUnlocked: false, justUnlocked: false, allergies: [], avoid: [], attrs: {}, note: "" };

  /* ── icons (currentColor line-art, matches theme aesthetic) ── */
  var ICON = {
    check: '<svg viewBox="0 0 24 24" fill="none" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M5 13l4 4L19 7"/></svg>'
  };

  /* ============================================================
     Accessibility — a single polite live region (created in init,
     lives on <body> so it survives Section Rendering swaps) announces
     cart changes to screen readers.
     ============================================================ */
  var liveRegion = null;
  function announce(msg) {
    if (!liveRegion || !msg) return;
    liveRegion.textContent = "";                                  // clear so a repeat of the same text re-announces
    setTimeout(function () { liveRegion.textContent = msg; }, 60);
  }
  /* announce an item-change to screen readers */
  function announceCartChange(prefix) {
    announce(prefix || "");
  }
  function lineName(row) {
    var el = row && row.querySelector(".skd-nm, .skc-nm");
    return el ? el.textContent.trim() : "Item";
  }

  /* ── cart extras: allergy chips + free-text note → cart.attributes + cart.note ──
     Strict allergies → cart.attributes['Allergies']; preferences → ['Prefer to avoid'];
     free text → cart.note. One debounced /cart/update.js POST persists all three. ── */
  var extrasTimer = null;
  function setNoteStatus(text, show) {
    $$("[data-note-status]").forEach(function (el) {
      el.textContent = text || "";
      el.classList.toggle("is-shown", !!show);
    });
  }
  function readExtrasFromDOM() {
    state.allergies = []; state.avoid = [];
    $$(".skx-chip[aria-pressed='true']").forEach(function (c) {
      var name = c.getAttribute("data-allergen");
      var arr = c.getAttribute("data-kind") === "avoid" ? state.avoid : state.allergies;
      if (name && arr.indexOf(name) === -1) arr.push(name);
    });
    // named free-text boxes (e.g. "Foods to avoid") → cart.attributes[name]
    state.attrs = {};
    $$("[data-cart-attr]").forEach(function (ta) {
      var k = ta.getAttribute("data-cart-attr");
      if (k) state.attrs[k] = ta.value;
    });
    // delivery box → cart.note. hasNote tracks whether a delivery box is on THIS
    // page: the Weekly Box card has none, so we must not blank an existing cart.note.
    var note = $("[data-cart-note]");
    state.hasNote = !!note;
    state.note = note ? note.value : "";
  }
  function mirrorChips() {
    $$(".skx-chip").forEach(function (c) {
      var arr = c.getAttribute("data-kind") === "avoid" ? state.avoid : state.allergies;
      c.setAttribute("aria-pressed", arr.indexOf(c.getAttribute("data-allergen")) !== -1 ? "true" : "false");
    });
    updateAllergyCounts();
  }
  /* "N selected" badge on the collapsed allergy disclosure — the only feedback a
     shopper gets once the <details> is closed again. */
  function updateAllergyCounts() {
    $$("[data-allergy-count]").forEach(function (b) {
      var root = b.closest(".skx-extras") || document;
      var n = root.querySelectorAll(".skx-chip[data-kind='allergy'][aria-pressed='true']").length;
      b.textContent = n + " selected";
      b.hidden = n === 0;
    });
    // folded preferences box: "Added" flag while it holds text
    $$("[data-pref-flag]").forEach(function (b) {
      var root = b.closest(".skx-extras") || document;
      var ta = root.querySelector("[data-cart-attr]");
      b.hidden = !(ta && ta.value.trim());
    });
  }
  function mirrorNote() {
    $$("[data-cart-note]").forEach(function (ta) {
      if (ta !== document.activeElement && ta.value !== state.note) ta.value = state.note;
    });
    $$("[data-cart-attr]").forEach(function (ta) {
      var v = state.attrs[ta.getAttribute("data-cart-attr")] || "";
      if (ta !== document.activeElement && ta.value !== v) ta.value = v;
    });
    updateAllergyCounts();
  }
  function scheduleSaveExtras() {
    setNoteStatus("", false);
    if (extrasTimer) clearTimeout(extrasTimer);
    extrasTimer = setTimeout(saveExtras, 550);
  }
  /* The delivery box lives in the cart only (moved off the Weekly Box page, Nov 2026). The customer-record
     filer reads the "Delivery instructions" LINE property of subscription orders (never the raw cart note),
     so copy the note onto every subscription line; otherwise renewals would lose it. Idempotent: it only
     writes a line whose property differs, one line per pass, and never re-renders (that would steal focus).
     A changed property gives the line a new key, so the rows on the page are told their new keys afterwards. */
  var DELIVERY_PROP = "Delivery instructions";
  function syncDeliveryToLines(pass) {
    if (!state.hasNote) return Promise.resolve();
    pass = pass || 0;
    if (pass > 6) return Promise.resolve();
    var text = (state.note || "").trim();
    return fetch(jsUrl(cfg.routes.cart, "/cart"), { headers: { Accept: "application/json" } })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        var item = (cart.items || []).filter(function (it) {
          if (!it.selling_plan_allocation) return false;
          var cur = (it.properties && it.properties[DELIVERY_PROP]) || "";
          return cur !== text;
        })[0];
        if (!item) { if (pass) updateLineKeys(cart); return; }
        var props = {};
        Object.keys(item.properties || {}).forEach(function (k) { props[k] = item.properties[k]; });
        props[DELIVERY_PROP] = text; // an empty string removes the property
        return fetch(jsUrl(cfg.routes.cartChange, "/cart/change"), {
          method: "POST",
          headers: { "Content-Type": "application/json", Accept: "application/json" },
          body: JSON.stringify({ id: item.key, quantity: item.quantity, properties: props })
        }).then(function () { return syncDeliveryToLines(pass + 1); });
      })
      .catch(function () { /* the cart note itself is already saved; a missed sync retries on the next save or render */ });
  }
  function updateLineKeys(cart) {
    $$("[data-line-item]").forEach(function (row) {
      var it = cart.items[Number(row.getAttribute("data-line")) - 1];
      if (!it) return;
      row.setAttribute("data-key", it.key);
      row.querySelectorAll("[data-key]").forEach(function (el) { el.setAttribute("data-key", it.key); });
    });
  }
  /* The delivery box was filled from the customer's account (sk-cart-extras): save it to the cart once with
     the '_delivery_prefill' marker, so clearing the box later sticks. An allergy prefill goes in the same
     request, because two cart writes at once can overwrite each other. Runs from afterRender, not an inline
     script, because a script inside a re-rendered section never runs. */
  function saveDeliveryPrefill(box) {
    box.removeAttribute("data-delivery-prefill");
    var attributes = { "_delivery_prefill": "1" };
    var allergy = $("[data-allergy-prefill]");
    if (allergy) {
      attributes["Allergies"] = allergy.getAttribute("data-allergy-prefill");
      attributes["_allergy_prefill"] = "1";
    }
    return fetch(jsUrl(cfg.routes.cartUpdate, "/cart/update"), {
      method: "POST",
      headers: { "Content-Type": "application/json", Accept: "application/json" },
      body: JSON.stringify({ note: box.value, attributes: attributes })
    })
      .then(function (r) { if (!r.ok) throw new Error("prefill " + r.status); return syncDeliveryToLines(); })
      .catch(function () { /* no marker was saved, so the next page load fills and saves it again */ });
  }
  function saveExtras() {
    extrasTimer = null;
    return fetch(jsUrl(cfg.routes.cartUpdate, "/cart/update"), {
      method: "POST",
      headers: { "Content-Type": "application/json", Accept: "application/json" },
      body: (function () {
        // "Prefer to avoid" (always empty) removed 2026-06-24: the old two-row card it
        // cleared is gone and nothing reads the key — it was only noise on every order.
        var attributes = { "Allergies": state.allergies.join(", ") };
        Object.keys(state.attrs).forEach(function (k) { attributes[k] = state.attrs[k]; });
        var payload = { attributes: attributes };
        // cart.note = the CANONICAL delivery instruction (reads as order.note; maps to
        // tsk.delivery_note via the planned Flow/n8n bridge). The Weekly Box ALSO attaches
        // a "Delivery instructions" LINE property so the instruction recurs in Recharge —
        // that is intentional, not a duplicate; do not consolidate them.
        // Only touch cart.note when a delivery box is actually on this page
        // (leave any existing note alone otherwise).
        if (state.hasNote) payload.note = state.note;
        return JSON.stringify(payload);
      })()
    })
      .then(function (r) { if (!r.ok) throw new Error("extras " + r.status); return r.json(); })
      .then(function () { return syncDeliveryToLines(); })
      .then(function () { setNoteStatus("Saved ✓", true); mirrorNote(); })
      .catch(function () { setNoteStatus("Couldn't save — try again", true); });
  }

  /* ============================================================
     Section Rendering API
     ============================================================ */
  function surfaces() { return $$("[data-sk-cart], [data-sk-cart-drawer]"); }
  function sectionIdOf(el) {
    var wrap = el.closest('[id^="shopify-section-"]');
    return wrap ? wrap.id.replace("shopify-section-", "") : null;
  }
  function sectionIds() {
    var ids = [];
    surfaces().forEach(function (el) { var id = sectionIdOf(el); if (id && ids.indexOf(id) === -1) ids.push(id); });
    return ids;
  }
  function applySections(sections) {
    if (!sections) return;
    Object.keys(sections).forEach(function (id) {
      var wrap = document.getElementById("shopify-section-" + id);
      if (wrap) wrap.innerHTML = sections[id];
    });
  }

  function fetchSections(path, ids) {
    var join = path.indexOf("?") === -1 ? "?" : "&";
    return fetch(path + join + "sections=" + ids.join(","), { headers: { Accept: "application/json" } })
      .then(function (r) { return r.json(); })
      .catch(function () { return null; });
  }
  function hasAnySection(s, ids) {
    return !!s && ids.some(function (id) { return typeof s[id] === "string"; });
  }
  function refreshSections(ids) {
    // Try the current URL first. On ?view= preview URLs (and some product
    // templates) the Section Rendering API returns the product's JSON instead
    // of rendered sections, so fall back to the site root — which always
    // renders the layout sections (the cart drawer) against the live cart.
    return fetchSections(window.location.pathname + window.location.search, ids)
      .then(function (s) {
        if (hasAnySection(s, ids)) { applySections(s); afterRender(); return; }
        var root = (window.Shopify && Shopify.routes && Shopify.routes.root) || "/";
        return fetchSections(root, ids).then(function (s2) {
          if (hasAnySection(s2, ids)) { applySections(s2); afterRender(); }
        });
      });
  }

  function mutate(endpoint, body) {
    var ids = sectionIds();
    body.sections = ids.join(",");
    body.sections_url = window.location.pathname;
    return fetch(endpoint, {
      method: "POST",
      headers: { "Content-Type": "application/json", Accept: "application/json" },
      body: JSON.stringify(body)
    })
      .then(function (r) { return r.json(); })
      .then(function (data) {
        if (data && data.sections) { applySections(data.sections); afterRender(); return data; }
        return refreshSections(ids).then(function () { return data; }); // fallback if response omits sections
      })
      .catch(function () { window.location.href = cfg.routes.cart || "/cart"; });
  }

  function changeLine(line, quantity) {
    return mutate(jsUrl(cfg.routes.cartChange, "/cart/change"), { line: line, quantity: quantity });
  }

  /* ============================================================
     Silent persist + targeted reconcile (optimistic ops only)
     ------------------------------------------------------------
     stepLine / removeLine already paint the row + subtotal + count +
     free-ship bar INSTANTLY (optimisticPaint). Persisting that change
     used to POST with a `sections` param and swap the whole section's
     innerHTML — which reset the drawer scroll, dropped focus and flashed
     the drawer closed/open ("reload" feel). Instead we POST WITHOUT
     sections (no HTML to swap) and reconcile only the handful of bits
     optimisticPaint can't compute — straight from the cart JSON this same
     request returns. A seq guard keeps only the latest response winning
     during rapid edits. optimisticPaint stays the SOLE owner of subtotal/
     count/header so a slow earlier response can't flicker a stale number.
     ============================================================ */
  var reconcileSeq = 0;
  function setShown(el, on) { if (el) el.style.display = on ? "" : "none"; } // inline beats the .skd-*/.skc-* display rules

  function mutateSilent(endpoint, body) {
    var seq = ++reconcileSeq;
    return fetch(endpoint, {
      method: "POST",
      headers: { "Content-Type": "application/json", Accept: "application/json" },
      body: JSON.stringify(body)
    })
      .then(function (r) { if (!r.ok) throw new Error("cart " + r.status); return r.json(); })
      .then(function (cart) { if (seq === reconcileSeq) reconcileSecondary(cart); return cart; })
      .catch(function () { if (seq === reconcileSeq) refreshSections(sectionIds()); }); // converge on error, no navigation
  }

  function reconcileSecondary(cart) {
    if (!cart || !cart.items) return;

    /* mirror the Liquid math in sk-cart-drawer.liquid / sk-cart.liquid */
    var savings = 0, hasSub = false, subTotal = 0, onetimeTotal = 0;
    cart.items.forEach(function (it) {
      var spa = it.selling_plan_allocation, save = 0;
      if (spa && spa.compare_at_price > spa.price) save = (spa.compare_at_price - spa.price) * it.quantity;
      else save = (it.original_line_price || 0) - (it.final_line_price || 0);
      if (save > 0) savings += save;
      if (spa) { hasSub = true; subTotal += it.final_line_price || 0; }
      else onetimeTotal += it.final_line_price || 0;
    });
    var isMixed = hasSub && onetimeTotal > 0;
    var cycle = cfg.recurringCycle || "/week";

    /* A. subscription footer — drawer (.skd-recur) */
    $$(".skd-recur").forEach(function (el) {
      setShown(el, hasSub);
      if (!hasSub) return;
      var t = el.querySelector(".skd-recur-t");
      if (t) t.innerHTML = "<b>" + money(subTotal) + cycle + "</b> " +
        (cfg.recurringShort || "renews automatically · cancel anytime") +
        (isMixed ? " · " + money(onetimeTotal) + " " + (cfg.onetimeShort || "one-time") : "");
    });
    /* A. subscription footer — cart page (.skc-recur) */
    $$(".skc-recur").forEach(function (el) {
      setShown(el, hasSub);
      if (!hasSub) return;
      var amt = el.querySelector(".skc-recur-amt");
      if (amt) amt.innerHTML = money(subTotal) + "<small>" + cycle + "</small>";
      var split = el.querySelector(".skc-recur-split");
      if (split) {
        setShown(split, isMixed);
        var spans = split.querySelectorAll("span");
        if (isMixed && spans[1]) spans[1].textContent = money(onetimeTotal);
      }
    });

    /* B. savings row — both surfaces */
    $$(".skd-row.save, .skc-trow.save").forEach(function (el) {
      setShown(el, savings > 0);
      if (savings > 0) { var v = el.querySelector(".v"); if (v) v.textContent = "−" + money(savings); }
    });

    /* C. grand total — cart page only */
    $$("[data-total]").forEach(function (el) { el.textContent = money(cart.total_price || 0); });

    /* D. per-line strikethrough was-price (matched by line key) */
    var byKey = {};
    cart.items.forEach(function (it) { byKey[it.key] = it; });
    $$("[data-line-item]").forEach(function (row) {
      var it = byKey[row.getAttribute("data-key")];
      if (!it) return; // row removed / not in this cart — leave to optimistic removal
      var spa = it.selling_plan_allocation, was = 0;
      if (spa && spa.compare_at_price > spa.price) was = spa.compare_at_price * it.quantity;
      else if (it.original_line_price > it.final_line_price) was = it.original_line_price;
      var wasEl = row.querySelector(".skd-was, .skc-was");
      if (wasEl) { if (was > 0) wasEl.textContent = money(was); setShown(wasEl, was > 0); }
    });

    /* E. checkout button disabled when empty — cart page <button data-checkout> */
    var empty = (cart.item_count || 0) === 0;
    $$("[data-checkout]").forEach(function (el) { el.disabled = empty; });

    /* F. cross-sell freshness: refresh the productIds island, then re-run reco
          (the recoPromises cache key includes sorted ids → new ids = cache miss) */
    var ids = cart.items.map(function (it) { return it.product_id; }).filter(function (x) { return x != null; });
    if (ids.length) {
      $$("[data-sk-reco-config]").forEach(function (node) {
        try { var rc = JSON.parse(node.textContent); rc.productIds = ids; node.textContent = JSON.stringify(rc); } catch (e) {}
      });
      loadAllReco();
    }
  }

  /* optimistic quantity: update the UI instantly, sync to Shopify after a short pause */
  var pendingQty = {};
  function optimisticPaint() {
    surfaces().forEach(function (el) {
      var sub = 0, count = 0;
      el.querySelectorAll("[data-line-item]").forEach(function (row) {
        if (row.classList.contains("is-removing")) return; // on its way out — exclude from totals
        var unit = Number(row.getAttribute("data-unit-price")) || 0;
        var qEl = row.querySelector("[data-qty]");
        var qty = qEl ? (Number(qEl.textContent) || 0) : 0;
        var lp = unit * qty;
        sub += lp; count += qty;
        var priceEl = row.querySelector(".skc-price, .skd-pr");
        if (priceEl) priceEl.textContent = money(lp);
      });
      el.setAttribute("data-cart-subtotal", sub);
      el.setAttribute("data-cart-count", count);
      el.setAttribute("data-cart-empty", count === 0 ? "true" : "false");
      var subEl = el.querySelector("[data-subtotal]");
      if (subEl) subEl.textContent = money(sub);
      var lbl = el.querySelector("[data-cart-count-label]");
      if (lbl) lbl.textContent = count > 0 ? "· " + count + (count === 1 ? " item" : " items") : "";
      paintSurface(el);
    });
    /* header badge from the freshest count — keeps every surface in lock-step */
    var counts = surfaces().map(function (el) { return Number(el.getAttribute("data-cart-count")) || 0; });
    updateHeaderCount(counts.length ? Math.max.apply(null, counts) : 0);

    /* track the free-delivery unlock crossing so callers can fold it into their announcement */
    var primary = surfaces()[0];
    if (primary) {
      var psub = Number(primary.getAttribute("data-cart-subtotal")) || 0;
      var pthr = Number(primary.getAttribute("data-threshold")) || cfg.threshold;
      var unlocked = pthr > 0 && psub >= pthr;
      if (unlocked && !state.freeUnlocked) state.justUnlocked = true;
      state.freeUnlocked = unlocked;
    }
  }
  function stepLine(row, delta) {
    var key = row.getAttribute("data-key");
    var qEl = row.querySelector("[data-qty]");
    if (!key || !qEl) return;
    var cur = pendingQty[key] ? pendingQty[key].qty : (Number(qEl.textContent) || 1);
    var next = Math.max(0, cur + delta);
    if (next === 0) { removeLine(row); return; }
    qEl.textContent = next;        // instant number
    optimisticPaint();            // instant prices + subtotal + count + free-ship bar
    announceCartChange(lineName(row) + ", quantity " + next);
    if (pendingQty[key] && pendingQty[key].timer) clearTimeout(pendingQty[key].timer);
    pendingQty[key] = { qty: next, timer: setTimeout(function () {
      var q = pendingQty[key] ? pendingQty[key].qty : next;
      delete pendingQty[key];
      mutateSilent(jsUrl(cfg.routes.cartChange, "/cart/change"), { id: key, quantity: q });
    }, 450) };
  }
  /* optimistic remove: drop the row from every surface and repaint instantly,
     then sync the quantity-0 change to Shopify after a short pause (same debounce
     pattern as a quantity step). mutateSilent persists + reconciles the footer
     bits without a full re-render (no flash/scroll-reset). */
  function removeLine(row) {
    var key = row.getAttribute("data-key");
    if (!key) return;
    var nm = lineName(row);
    if (pendingQty[key] && pendingQty[key].timer) clearTimeout(pendingQty[key].timer);
    surfaces().forEach(function (el) {
      el.querySelectorAll("[data-line-item]").forEach(function (r) {
        if (r.getAttribute("data-key") !== key) return;
        r.classList.add("is-removing");                                   // fade/slide out (CSS .3s)
        setTimeout(function () { if (r.parentNode) r.parentNode.removeChild(r); }, 300);
      });
    });
    optimisticPaint();   // subtotal + count + empty-state + header, minus the removed line
    announce(nm + " removed from your bag");
    pendingQty[key] = { qty: 0, timer: setTimeout(function () {
      delete pendingQty[key];
      mutateSilent(jsUrl(cfg.routes.cartChange, "/cart/change"), { id: key, quantity: 0 });
    }, 320) };
  }
  function addVariant(id) {
    return mutate(jsUrl(cfg.routes.cartAdd, "/cart/add"), { id: Number(id), quantity: 1 });
  }

  /* Generic inline variant swap: change any line item to the chosen variant.
     Adds the new variant (carrying the line's quantity, subscription plan and
     properties), then removes the old line and re-renders. Used by the per-line
     [data-sk-varsel] dropdown (meal tier, gift-card amount, apparel size, …). */
  function swapLineSelect(sel) {
    var key = sel.getAttribute("data-key");
    var cur = sel.getAttribute("data-cur");
    var newId = sel.value;
    if (!newId || !key || String(newId) === String(cur)) return;
    var qty = Number(sel.getAttribute("data-qty")) || 1;
    var plan = sel.getAttribute("data-plan");

    /* preserve line-item properties (e.g. a gift card's recipient details).
       Liquid's `item.properties | json` serialises as an ARRAY of [key,value]
       pairs, not an object — /cart/add wants an object, so convert. (Skipping
       this silently drops a gift card's recipient info on an amount change.) */
    var props = null;
    var lineEl = sel.closest("[data-line-item]");
    var pj = lineEl && lineEl.querySelector("[data-sk-lineprops]");
    if (pj) {
      try {
        var parsed = JSON.parse(pj.textContent);
        if (Array.isArray(parsed) && parsed.length) {
          props = {};
          parsed.forEach(function (pair) { if (pair && pair.length === 2 && pair[1] !== "") props[pair[0]] = pair[1]; });
        } else if (parsed && typeof parsed === "object" && Object.keys(parsed).length) {
          props = parsed;
        }
        if (props && !Object.keys(props).length) props = null;
      } catch (e) {}
    }

    sel.disabled = true;
    var line = { id: Number(newId), quantity: qty };
    if (plan) line.selling_plan = Number(plan);
    if (props) line.properties = props;

    return fetch(jsUrl(cfg.routes.cartAdd, "/cart/add"), {
      method: "POST", headers: { "Content-Type": "application/json", Accept: "application/json" }, body: JSON.stringify({ items: [line] })
    })
      .then(function (r) { return r.json(); })
      .then(function () { return mutate(jsUrl(cfg.routes.cartChange, "/cart/change"), { id: key, quantity: 0 }); })
      .catch(function () { window.location.href = cfg.routes.cart || "/cart"; });
  }

  /* ============================================================
     Paint dynamic bits after each (re)render
     ============================================================ */
  function shipMessage(remaining, isPickup) {
    if (isPickup) return ICON.check + " Free local pickup";
    return "Delivered fresh every Friday · NZ-wide";
  }

  function paintSurface(el) {
    var subtotal = Number(el.getAttribute("data-cart-subtotal")) || 0;
    var threshold = Number(el.getAttribute("data-threshold")) || cfg.threshold;
    var isPickup = state.fulfil === "pickup";
    var remaining = Math.max(0, threshold - subtotal);

    var fill = $("[data-ship-fill]", el);
    if (fill) fill.style.width = Math.min(100, threshold ? (subtotal / threshold) * 100 : 100) + "%";
    var amt = $("[data-ship-amt]", el);
    if (amt) amt.textContent = money(Math.min(subtotal, threshold)) + " / " + money(threshold);
    var msg = $("[data-ship-msg]", el);
    if (msg) msg.innerHTML = shipMessage(remaining, isPickup);

    /* delivery/pickup informational row (does NOT change the real total) */
    var dl = $("[data-deliv-label]", el);
    var dv = $("[data-deliv-value]", el);
    if (dl) dl.textContent = isPickup ? "Pickup" : "Delivery";
    if (dv) dv.textContent = isPickup ? "Free" : (el.getAttribute("data-shipping-hint") || dv.textContent);

    /* reflect fulfilment toggle button state */
    $$("[data-fulfil]", el).forEach(function (b) {
      var on = b.getAttribute("data-fulfil") === state.fulfil;
      b.setAttribute("aria-pressed", on);
      b.setAttribute("aria-checked", on);
    });
    var pickbox = $("[data-pickbox]", el);
    if (pickbox) pickbox.hidden = !isPickup;
    var delivbox = $("[data-delivbox]", el);
    if (delivbox) delivbox.style.display = isPickup ? "none" : "";
  }

  function updateHeaderCount(count) {
    var badge = $(".sk-header__cart-count");
    var link = $('.sk-header__icon[aria-label^="Cart"]');
    if (badge) {
      if (count > 0) { badge.textContent = count; badge.hidden = false; badge.style.display = ""; }
      else { badge.style.display = "none"; }
    } else if (link && count > 0) {
      var s = document.createElement("span");
      s.className = "sk-header__cart-count";
      s.textContent = count;
      link.appendChild(s);
    }
  }

  /* ============================================================
     Adaptive cross-sell — Shopify Product Recommendations API.
     Replaces the static fallback collection with picks that adapt
     to what's actually in the cart: complementary ("goes well with",
     curated in Search & Discovery) first, then related (Shopify's
     buy-together algorithm) to top up. Re-runs after every Section
     Rendering swap; results are cached by cart-product signature so
     quantity changes don't refetch. If nothing comes back, the
     server-rendered static collection stays as the fallback.
     ============================================================ */
  var PLUS_SVG = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M12 5v14M5 12h14"/></svg>';
  var recoPromises = {}; // signature -> Promise<products[]>

  function esc(s) { return String(s == null ? "" : s).replace(/[&<>"]/g, function (c) { return { "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" }[c]; }); }
  function sized(url, w) { if (!url) return ""; return url + (url.indexOf("?") > -1 ? "&" : "?") + "width=" + w; }
  function uniq(arr) { var seen = {}, out = []; (arr || []).forEach(function (x) { if (!seen[x]) { seen[x] = 1; out.push(x); } }); return out; }
  function firstVariant(p) { var vs = p.variants || []; for (var i = 0; i < vs.length; i++) { if (vs[i].available) return vs[i]; } return null; }

  function readReco(container) {
    var node = container.querySelector("[data-sk-reco-config]");
    if (!node) return null;
    try { return JSON.parse(node.textContent); } catch (e) { return null; }
  }
  function recoFetch(url) {
    return fetch(url, { headers: { Accept: "application/json" } })
      .then(function (r) { return r.json(); })
      .then(function (d) { return (d && d.products) || []; })
      .catch(function () { return []; });
  }
  function fetchIntent(url, basis, intent, limit) {
    var lim = Math.min(10, (limit || 3) + 4);
    return Promise.all(basis.map(function (id) {
      return recoFetch(url + ".json?product_id=" + id + "&intent=" + intent + "&limit=" + lim);
    }));
  }
  function mergePool(pool, lists, baseScore, exclude) {
    lists.forEach(function (list) {
      list.forEach(function (p, idx) {
        if (!p || exclude[p.id] || p.available === false) return;
        var e = pool[p.id] || (pool[p.id] = { p: p, score: 0 });
        e.score += baseScore + Math.max(0, 10 - idx); // recommended by more cart items / higher up = stronger
      });
    });
  }
  function finalizePool(pool, limit) {
    return Object.keys(pool).map(function (k) { return pool[k]; })
      .sort(function (a, b) { return b.score - a.score; })
      .slice(0, limit)
      .map(function (e) { return e.p; });
  }
  function buildPool(cfg2, basis, exclude) {
    var pool = {};
    var wantComp = cfg2.strategy !== "related";
    var wantRel = cfg2.strategy !== "complementary";
    var first = wantComp
      ? fetchIntent(cfg2.recommendUrl, basis, "complementary", cfg2.limit).then(function (lists) { mergePool(pool, lists, 100, exclude); })
      : Promise.resolve();
    return first.then(function () {
      if (!wantRel || Object.keys(pool).length >= cfg2.limit) return finalizePool(pool, cfg2.limit);
      return fetchIntent(cfg2.recommendUrl, basis, "related", cfg2.limit).then(function (lists) {
        mergePool(pool, lists, 10, exclude);
        return finalizePool(pool, cfg2.limit);
      });
    });
  }

  function cardPage(p) {
    var v = firstVariant(p), img = sized(p.featured_image, 360);
    return '<div class="skc-cs">' +
      '<div class="skc-ci">' + (img ? '<img src="' + img + '" width="100" height="70" loading="lazy" alt="' + esc(p.title) + '">' : "") +
        (v ? '<button type="button" class="skc-ci-add" data-add="' + v.id + '" aria-label="Add ' + esc(p.title) + ' to your bag">' + PLUS_SVG + "<span>Add</span></button>" : "") +
      "</div>" +
      '<div class="skc-cb">' +
        '<a class="skc-cn" href="' + p.url + '">' + esc(p.title) + "</a>" +
        (p.type ? '<div class="skc-cm">' + esc(p.type) + "</div>" : "") +
        '<span class="skc-cp">' + (p.price_varies ? "from " : "") + money(p.price_min) + "</span>" +
      "</div>" +
    "</div>";
  }
  function cardDrawer(p, addLabel) {
    var v = firstVariant(p); if (!v) return "";
    var img = sized(p.featured_image, 96);
    return '<div class="skd-ac">' +
      '<a class="skd-ac-thumb" href="' + p.url + '">' + (img ? '<img src="' + img + '" width="42" height="42" loading="lazy" alt="' + esc(p.title) + '">' : "") + "</a>" +
      '<div class="skd-ac-info">' +
        '<span class="skd-an">' + esc(p.title) + "</span>" +
        '<span class="skd-ap">' + (p.price_varies ? "from " : "") + money(p.price_min) + "</span>" +
      "</div>" +
      '<button type="button" class="skd-aadd" data-add="' + v.id + '" aria-label="Add ' + esc(p.title) + '">' + PLUS_SVG + " " + esc(addLabel || "Add") + "</button>" +
    "</div>";
  }

  function renderReco(container, cfg2, products) {
    var list = container.querySelector("[data-reco-list]");
    if (!list) return;
    list.innerHTML = products.map(function (p) {
      return cfg2.surface === "drawer" ? cardDrawer(p, cfg2.addLabel) : cardPage(p);
    }).join("");
    container.hidden = false;
  }
  function hideIfNoFallback(container) {
    if (container.getAttribute("data-reco-hasfallback") === "true") return; // keep static cards
    var list = container.querySelector("[data-reco-list]");
    if (!list || list.children.length === 0) container.hidden = true;
  }
  function loadReco(container) {
    var cfg2 = readReco(container);
    if (!cfg2 || !cfg2.recommendUrl) return;
    var ids = uniq(cfg2.productIds || []);
    if (!ids.length) { hideIfNoFallback(container); return; }
    var basis = ids.slice(0, cfg2.maxBasis || 4);
    // Superset rule: if a "contains everything" bundle (e.g. the recipe bundle)
    // is in the cart, exclude every product it supersedes (the cookbook
    // collection) so we don't recommend a recipe the customer already gets
    // inside the bundle. supersetIds/bundleId are static per render, so this
    // recomputes correctly on every silent reconcile too.
    var hasBundle = cfg2.bundleId != null && ids.indexOf(cfg2.bundleId) > -1;
    var sig = cfg2.surface + "|" + cfg2.strategy + "|" + cfg2.limit + "|" + ids.slice().sort().join(",") + "|b" + (hasBundle ? 1 : 0);
    var exclude = {}; ids.forEach(function (id) { exclude[id] = true; });
    if (hasBundle) (cfg2.supersetIds || []).forEach(function (id) { exclude[id] = true; });
    var job = recoPromises[sig] || (recoPromises[sig] = buildPool(cfg2, basis, exclude));
    job.then(function (products) {
      if (products && products.length) renderReco(container, cfg2, products);
      else hideIfNoFallback(container);
    }).catch(function () { /* keep static fallback */ });
  }
  function loadAllReco() { $$("[data-sk-reco]").forEach(loadReco); }

  function afterRender() {
    surfaces().forEach(function (el) {
      // Keep the slide-in DRAWER display:none while it's closed so a silent
      // re-render (e.g. after an add elsewhere) can't flash it in/out. The cart
      // PAGE surface stays visible. openDrawer() un-hides the drawer itself.
      var isDrawer = el.hasAttribute("data-sk-cart-drawer");
      el.hidden = isDrawer ? !state.drawerOpen : false;
      paintSurface(el);
    });
    var scrim = $("[data-sk-cart-scrim]");
    if (scrim) scrim.hidden = !state.drawerOpen;

    /* restore drawer open state after a re-render */
    var drawer = $("[data-sk-cart-drawer]");
    if (drawer && state.drawerOpen) { drawer.classList.add("is-open"); if (scrim) scrim.classList.add("is-open"); }

    /* header count from the freshest surface */
    var counts = surfaces().map(function (el) { return Number(el.getAttribute("data-cart-count")) || 0; });
    updateHeaderCount(counts.length ? Math.max.apply(null, counts) : 0);

    tickCountdown();
    loadAllReco(); // adapt cross-sell to the freshly-rendered cart contents
    readExtrasFromDOM(); // re-sync allergy/note state from the freshly-rendered (server-authoritative) chips
    var prefillBox = $("[data-delivery-prefill]");
    if (prefillBox) saveDeliveryPrefill(prefillBox);
    else syncDeliveryToLines();
  }

  /* ============================================================
     Drawer open / close
     ============================================================ */
  function getDrawer() { return $("[data-sk-cart-drawer]"); }
  function getScrim() { return $("[data-sk-cart-scrim]"); }

  function openDrawer() {
    var drawer = getDrawer(), scrim = getScrim();
    if (!drawer) { window.location.href = cfg.routes.cart || "/cart"; return; }
    state.lastFocus = document.activeElement;
    drawer.hidden = false; if (scrim) scrim.hidden = false;
    /* allow the un-hide to commit before transitioning */
    requestAnimationFrame(function () {
      drawer.classList.add("is-open");
      if (scrim) scrim.classList.add("is-open");
    });
    document.body.style.overflow = "hidden";
    state.drawerOpen = true;
    var focusable = $(".skd-close", drawer);
    if (focusable) focusable.focus();
  }
  function closeDrawer() {
    var drawer = getDrawer(), scrim = getScrim();
    if (drawer) drawer.classList.remove("is-open");
    if (scrim) scrim.classList.remove("is-open");
    document.body.style.overflow = "";
    state.drawerOpen = false;
    if (state.lastFocus && state.lastFocus.focus) state.lastFocus.focus();
  }

  /* ============================================================
     Event delegation (survives Section Rendering swaps)
     ============================================================ */
  document.addEventListener("change", function (e) {
    /* gift-card add-on: keep the Add button's data-add in sync with the chosen
       amount, so the existing [data-add] click handler adds the right variant. */
    var gcs = e.target.closest("[data-sk-gcadd-sel]");
    if (gcs) {
      var box = gcs.closest("[data-sk-gcadd]");
      var btn = box && box.querySelector("[data-sk-gcadd-btn]");
      if (btn) btn.setAttribute("data-add", gcs.value);
      return;
    }
    /* inline variant editor: swap a line item to the chosen variant. */
    var vsel = e.target.closest("[data-sk-varsel]");
    if (vsel) { swapLineSelect(vsel); return; }
  });

  document.addEventListener("click", function (e) {
    /* open drawer from header bag (progressive enhancement) */
    var bag = e.target.closest('.sk-header__icon[aria-label^="Cart"], [data-sk-cart-open]');
    if (bag && getDrawer() && !e.metaKey && !e.ctrlKey && !e.shiftKey && e.button === 0) {
      e.preventDefault();
      openDrawer();
      return;
    }

    if (e.target.closest("[data-sk-cart-close]")) { e.preventDefault(); closeDrawer(); return; }
    if (e.target.closest("[data-sk-cart-scrim]")) { closeDrawer(); return; }

    /* allergy chip toggle — DOM-authoritative so it survives pre-filled
       (server-rendered) chips and stays in sync across duplicate surfaces
       (cart page + Weekly Box buy column) */
    var chip = e.target.closest(".skx-chip");
    if (chip) {
      var aName = chip.getAttribute("data-allergen");
      var wasOn = chip.getAttribute("aria-pressed") === "true";
      chip.setAttribute("aria-pressed", wasOn ? "false" : "true");
      readExtrasFromDOM();      // rebuild state from every currently-pressed chip
      mirrorChips();            // mirror to any other surface showing the card
      announce(aName + (wasOn ? " removed" : " added to allergies"));
      scheduleSaveExtras();
      return;
    }

    /* quantity stepper (optimistic + debounced) */
    var step = e.target.closest("[data-step]");
    if (step) {
      var row = step.closest("[data-line-item]");
      if (row) stepLine(row, Number(step.getAttribute("data-step")));
      return;
    }

    /* remove (optimistic) */
    var rm = e.target.closest("[data-remove]");
    if (rm) {
      var rRow = rm.closest("[data-line-item]");
      if (rRow) removeLine(rRow);
      return;
    }

    /* cross-sell add */
    var add = e.target.closest("[data-add]");
    if (add) {
      add.classList.add("is-added");
      add.innerHTML = ICON.check;
      var addName = (add.getAttribute("aria-label") || "Item").replace(/^add\s+/i, "");
      announce(addName + " added to your bag");
      addVariant(add.getAttribute("data-add"));
      return;
    }

    /* fulfilment toggle */
    var fb = e.target.closest("[data-fulfil]");
    if (fb) {
      state.fulfil = fb.getAttribute("data-fulfil");
      surfaces().forEach(paintSurface);
      return;
    }

    /* promo apply */
    var pa = e.target.closest("[data-promo-apply]");
    if (pa) { applyPromo(pa.closest("[data-sk-cart]") || document); return; }

    /* checkout — route through discount URL when a promo is set */
    var co = e.target.closest("[data-checkout]");
    if (co && state.promo) {
      e.preventDefault();
      window.location.href = "/discount/" + encodeURIComponent(state.promo) + "?redirect=/checkout";
      return;
    }
  });

  document.addEventListener("keydown", function (e) {
    if (e.key === "Escape" && state.drawerOpen) { closeDrawer(); return; }
    if (e.key === "Tab" && state.drawerOpen) { trapTab(e); return; }
    if (e.key === "Enter" && e.target.closest("[data-promo-input]")) {
      e.preventDefault();
      applyPromo(e.target.closest("[data-sk-cart]") || document);
    }
  });

  /* keep keyboard focus inside the open drawer (Tab / Shift+Tab wrap) */
  function trapTab(e) {
    var drawer = getDrawer();
    if (!drawer) return;
    var focusables = $$('a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), summary, [tabindex]:not([tabindex="-1"])', drawer)
      .filter(function (el) { return el.offsetParent !== null; }); // skip hidden (closed <details>, empty-state, etc.)
    if (!focusables.length) return;
    var first = focusables[0], last = focusables[focusables.length - 1];
    if (!drawer.contains(document.activeElement)) { e.preventDefault(); first.focus(); return; }
    if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
    else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
  }

  /* free-text boxes (avoid + delivery): autosave (debounced) */
  document.addEventListener("input", function (e) {
    var ta = e.target && e.target.closest ? e.target.closest("[data-cart-note], [data-cart-attr]") : null;
    if (!ta) return;
    readExtrasFromDOM();
    updateAllergyCounts();
    scheduleSaveExtras();
  });
  /* flush immediately if they leave the field before the debounce fires */
  document.addEventListener("focusout", function (e) {
    var ta = e.target && e.target.closest ? e.target.closest("[data-cart-note], [data-cart-attr]") : null;
    if (!ta) return;
    readExtrasFromDOM();
    if (extrasTimer) { clearTimeout(extrasTimer); extrasTimer = null; saveExtras(); }
  });

  function applyPromo(scope) {
    var input = $("[data-promo-input]", scope);
    var msg = $("[data-promo-msg]", scope);
    if (!input || !msg) return;
    var code = input.value.trim().toUpperCase();
    if (!code) { state.promo = null; msg.className = "skc-promo-msg"; msg.textContent = ""; return; }
    state.promo = code;
    msg.className = "skc-promo-msg is-ok";
    msg.innerHTML = ICON.check + " " + code + " will apply at checkout";
  }

  /* ============================================================
     Countdown to weekly order cutoff
     ============================================================ */
  // NZ-clock cut-off from snippets/sk-nz-cutoff (never the visitor's clock).
  function nextCutoff() {
    return window.SKCutoff ? SKCutoff.next(cfg.cutoffWeekday, cfg.cutoffHour, cfg.cutoffMinute) : null;
  }
  var cutoffTarget = nextCutoff();
  function tickCountdown() {
    if (!cutoffTarget) return;
    var ms = cutoffTarget.ms - Date.now();
    if (ms < 0) { cutoffTarget = nextCutoff(); ms = cutoffTarget.ms - Date.now(); }
    var s = Math.floor(ms / 1000);
    var d = Math.floor(s / 86400), h = Math.floor((s % 86400) / 3600), m = Math.floor((s % 3600) / 60);
    var text = d + "d " + h + "h " + String(m).padStart(2, "0") + "m";
    $$("[data-countdown]").forEach(function (el) { el.textContent = text; });
    // Delivery Friday = this cutoff's NZ date (Tue) + 3 days. Rolls weekly on its own.
    var dtext = SKCutoff.label(cutoffTarget, 3);
    $$("[data-delivery-date]").forEach(function (el) { el.textContent = dtext; });
  }

  /* ============================================================
     Init
     ============================================================ */
  function init() {
    surfaces().forEach(function (el) { el.hidden = false; });
    var scrim = getScrim(); if (scrim) scrim.hidden = false;

    /* polite live region for screen-reader announcements (one per page, on <body>
       so Section Rendering swaps inside the drawer never wipe it) */
    if (!liveRegion) {
      liveRegion = document.createElement("div");
      liveRegion.setAttribute("role", "status");
      liveRegion.setAttribute("aria-live", "polite");
      liveRegion.setAttribute("aria-atomic", "true");
      liveRegion.style.cssText = "position:absolute;width:1px;height:1px;margin:-1px;padding:0;overflow:hidden;clip:rect(0 0 0 0);white-space:nowrap;border:0;";
      document.body.appendChild(liveRegion);
    }
    afterRender();
    tickCountdown();
    setInterval(tickCountdown, 1000);

    /* Public API + cross-script events so any add-to-cart code (snacks grid,
       PDP, weekly box) can keep the drawer/header in sync:
         document.dispatchEvent(new CustomEvent('sk-cart:refresh'))   // silent sync
         document.dispatchEvent(new CustomEvent('sk-cart:added'))     // silent sync (after an add)
         document.dispatchEvent(new CustomEvent('sk-cart:open'))      // explicitly slide the drawer in
       Owner pref: adding to cart CONFIRMS (each section shows a toast / "Added ✓"
       button + the header badge updates) but does NOT auto-open the drawer, so
       shoppers can keep browsing. The legacy {detail:{open:true}} flag on
       'sk-cart:added' is intentionally ignored — fire 'sk-cart:open' to open. */
    window.SKCart = { open: openDrawer, close: closeDrawer, refresh: function () { return refreshSections(sectionIds()); } };
    document.addEventListener("sk-cart:refresh", function () { refreshSections(sectionIds()); });
    document.addEventListener("sk-cart:open", function () { openDrawer(); });
    document.addEventListener("sk-cart:added", function () { refreshSections(sectionIds()); });
  }
  if (document.readyState === "loading") document.addEventListener("DOMContentLoaded", init);
  else init();
})();
